"use client";

import { Button, Input, Loader, Modal, toast } from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import type { PosSaleDetailResponse } from "@/modules/merchant/presentation/api/client";
import {
  usePosAddSalePaymentsMutation,
  usePosSaleDetailQuery,
} from "@/modules/merchant/presentation/api/queries";
import { formatPrice } from "@/shared/lib/currency";

function parseMoneyInput(s: string): number {
  const normalized = s.replace(/,/g, "").trim();
  if (normalized === "" || normalized === ".") return 0;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

function sanitizeMoneyDecimalTyping(raw: string): string {
  const s = raw
    .replace(/,/g, "")
    .trim()
    .replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot === -1) return s;
  const intPart = s.slice(0, firstDot);
  const after = s.slice(firstDot + 1).replace(/\./g, "");
  return `${intPart}.${after.slice(0, 2)}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function sumPaymentsPaid(detail: PosSaleDetailResponse): number {
  return round2(
    detail.payments.reduce((s, p) => {
      const inv =
        p.invoiceEquivalentAmount != null &&
        Number.isFinite(p.invoiceEquivalentAmount)
          ? p.invoiceEquivalentAmount
          : Number.isFinite(p.amount)
            ? p.amount
            : 0;
      return s + inv;
    }, 0),
  );
}

type Props = {
  shopId: string | undefined;
  branchId: string | undefined;
  saleId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ShopPosSettlePartialModal({
  shopId,
  branchId,
  saleId,
  open,
  onOpenChange,
}: Props) {
  const detailQ = usePosSaleDetailQuery(
    shopId,
    saleId ?? undefined,
    Boolean(open && shopId && saleId),
  );
  const addPay = usePosAddSalePaymentsMutation(shopId, branchId);

  const [payMethod, setPayMethod] = useState<"cash" | "transfer" | "wallet">(
    "cash",
  );
  const [amountDraft, setAmountDraft] = useState("");
  const [payRef, setPayRef] = useState("");

  useEffect(() => {
    if (!open) return;
    setPayMethod("cash");
    setAmountDraft("");
    setPayRef("");
  }, [open]);

  const detail = detailQ.data;
  const paidSum = detail ? sumPaymentsPaid(detail) : 0;
  const outstanding = detail ? round2(Math.max(0, detail.total - paidSum)) : 0;

  const amountNum = useMemo(() => parseMoneyInput(amountDraft), [amountDraft]);

  const canSubmit =
    Boolean(shopId && branchId && saleId && detail) &&
    detail.status === "partial" &&
    outstanding > 0.009 &&
    amountNum > 0 &&
    (payMethod !== "transfer" || payRef.trim().length > 0);

  const submit = async () => {
    if (!shopId || !branchId || !saleId || !detail) return;
    if (detail.status !== "partial") {
      toast.error("ບິນນີ້ບໍ່ໃຊ້ໂໝດຊຳລະຕໍ່");
      return;
    }
    if (amountNum <= 0) {
      toast.error("ໃສ່ຍອດຊຳລະ");
      return;
    }
    if (payMethod === "transfer" && !payRef.trim()) {
      toast.error("ໂອນຕ້ອງມີເລກອ້າງອີງ");
      return;
    }
    try {
      const res = await addPay.mutateAsync({
        saleId,
        body: {
          payments: [
            {
              method: payMethod,
              amount: amountNum,
              ...(payMethod === "transfer" ? { reference: payRef.trim() } : {}),
            },
          ],
        },
      });
      if (res.saleStatus === "paid") {
        toast.success("ຊຳລະຄົບ — ປິດບິນແລ້ວ");
      } else {
        toast.success("ບັນທຶກຊຳລະແລ້ວ — ຍັງມີຍອດຄ້າງ");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຊຳລະລົ້ມເຫຼວ");
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ຊຳລະສ່ວນທີ່ເຫຼືອ"
      description="ບິນມັດຈຳ — ເພີ່ມການຊຳລະຈົນກວ່າຍອດຄົບຕາມກົດລະບົບ"
      size="md"
    >
      {detailQ.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : !detail ? (
        <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນບິນ</p>
      ) : detail.status !== "partial" ? (
        <p className="text-muted-foreground text-sm">ບິນນີ້ບໍ່ຢູ່ໃນສະຖານະຊຳລະບາງສ່ວນ</p>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/25 px-3 py-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">ຍອດບິນ</span>
              <span className="font-semibold tabular-nums">
                {formatPrice(detail.total, detail.currency)}
              </span>
            </div>
            <div className="mt-1 flex justify-between gap-3">
              <span className="text-muted-foreground">ຊຳລະແລ້ວ</span>
              <span className="tabular-nums">
                {formatPrice(paidSum, detail.currency)}
              </span>
            </div>
            <div className="mt-2 flex justify-between gap-3 border-border border-t pt-2 font-heading font-semibold">
              <span>ຄ້າງຊຳລະ</span>
              <span className="text-secondary tabular-nums">
                {formatPrice(outstanding, detail.currency)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {(
              [
                { k: "cash" as const, label: "ເງິນສົດ" },
                { k: "transfer" as const, label: "ໂອນ" },
                { k: "wallet" as const, label: "Wallet" },
              ] as const
            ).map(({ k, label }) => (
              <Button
                key={k}
                type="button"
                size="sm"
                variant={payMethod === k ? "default" : "outline"}
                className="h-10 font-medium text-xs"
                onClick={() => setPayMethod(k)}
              >
                {label}
              </Button>
            ))}
          </div>

          <div>
            <label
              htmlFor="pos-settle-amount"
              className="mb-1.5 block font-medium text-card-foreground text-xs"
            >
              ຍອດຊຳລະ ({detail.currency})
            </label>
            <Input
              id="pos-settle-amount"
              inputMode="decimal"
              autoComplete="off"
              value={amountDraft}
              onChange={(e) =>
                setAmountDraft(sanitizeMoneyDecimalTyping(e.target.value))
              }
              className="h-11 font-mono text-base tabular-nums"
              placeholder="0"
            />
          </div>

          {payMethod === "transfer" ? (
            <div>
              <label
                htmlFor="pos-settle-ref"
                className="mb-1.5 block font-medium text-card-foreground text-xs"
              >
                ເລກອ້າງອີງ
              </label>
              <Input
                id="pos-settle-ref"
                value={payRef}
                onChange={(e) => setPayRef(e.target.value)}
                placeholder="ຕົວຢ່າງ: ref ຈາກທະນາຄານ"
                className="h-10 font-mono text-sm"
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={addPay.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              className="font-semibold"
              onClick={() => void submit()}
              disabled={!canSubmit || addPay.isPending}
            >
              {addPay.isPending ? <Loader /> : null}
              ຢືນຢັນຊຳລະ
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
